import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    ChevronLeft, RefreshCw, ImagePlus, Wand2, Palette, Link2, UploadCloud,
    AlertTriangle, ImageOff, HardDrive, Users2, Loader2, FileImage,
} from "lucide-react";

/**
 * PhotoDashboard
 *
 * Single-pane admin view over the whole photo pipeline. Consolidates:
 *   • Counters: with/without photo, missing (DB pointer → no file), unlinked
 *     (file in storage → no DB pointer), CMYK conversions pending, total bytes.
 *   • Actions: rescan health, run color-fix, run normalize, run link, browse.
 *   • Job cards: live status of background jobs (normalize, colorfix, drive sync).
 *
 * Fetches `/api/admin/photos/summary` on mount and every 8s while any bg job
 * is running.
 */
function fmtBytes(n) {
    if (!n || n < 1024) return `${n || 0} B`;
    const kb = n / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
}

// Safely render backend error payloads (string, Pydantic array, or object).
function fmtErr(d) {
    if (!d) return "Something went wrong";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
    return d?.msg || String(d);
}

function StatCard({ icon, label, value, sub, tone = "default", testid }) {
    const tones = {
        default: "bg-white border-slate-200",
        good: "bg-emerald-50 border-emerald-200",
        warn: "bg-amber-50 border-amber-200",
        bad: "bg-red-50 border-red-200",
    };
    return (
        <Card className={`p-4 border ${tones[tone]}`} data-testid={testid}>
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
                    <div className="mt-1 text-2xl font-semibold text-slate-800">{value}</div>
                    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
                </div>
                <div className="text-slate-400">{icon}</div>
            </div>
        </Card>
    );
}

function JobCard({ title, job, testid }) {
    if (!job || job.state === "idle") {
        return (
            <Card className="p-4 border border-slate-200" data-testid={testid}>
                <div className="text-sm font-medium text-slate-800">{title}</div>
                <div className="mt-1 text-xs text-slate-500">Idle · never started this session</div>
            </Card>
        );
    }
    const pct = job.total ? Math.round((job.processed / job.total) * 100) : 0;
    const stateColors = {
        running: "text-blue-600",
        done: "text-emerald-600",
        error: "text-red-600",
    };
    return (
        <Card className="p-4 border border-slate-200" data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-800">{title}</div>
                <span className={`text-xs font-semibold uppercase ${stateColors[job.state] || "text-slate-500"}`}>{job.state}</span>
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                    className={`h-full ${job.state === "error" ? "bg-red-500" : "bg-rotary-blue"}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="mt-2 text-xs text-slate-600 flex flex-wrap gap-x-3 gap-y-1">
                <span>{job.processed}/{job.total} processed</span>
                {job.renamed != null && <span>· {job.renamed} renamed</span>}
                {job.converted != null && <span>· {job.converted} converted</span>}
                {job.downloaded != null && <span>· {job.downloaded} downloaded</span>}
                {job.linked != null && <span>· {job.linked} linked</span>}
                {job.skipped != null && <span>· {job.skipped} skipped</span>}
                {job.missing != null && <span>· {job.missing} missing</span>}
                {job.errors != null && <span>· {job.errors} errors</span>}
            </div>
            {job.error && (
                <div className="mt-2 text-xs text-red-600" data-testid={`${testid}-error`}>
                    {job.error}
                </div>
            )}
        </Card>
    );
}

export default function PhotoDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState({});
    const [bulkUploading, setBulkUploading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const bulkInputRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/photos/summary");
            setData(data);
            setLoading(false);
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Failed to load summary");
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(() => {
            // Only poll while a job is running to avoid unnecessary traffic
            const running = data && Object.values(data.jobs || {}).some(j => j?.state === "running");
            if (running) load();
        }, 6000);
        return () => clearInterval(t);
    }, [data?.jobs?.normalize?.state, data?.jobs?.colorfix?.state, data?.jobs?.drive_photos?.state, data?.jobs?.claim?.state]);

    const action = async (key, path, method = "POST", pollKey = null) => {
        setBusy(b => ({ ...b, [key]: true }));
        try {
            const call = method === "POST" ? api.post(path) : api.get(path);
            await call;
            toast.info(`${key} started — refresh in a moment for progress`);
            await load();
            // Poll status via load()
            if (pollKey) {
                const started = Date.now();
                while (Date.now() - started < 6 * 60 * 1000) {
                    await new Promise(r => setTimeout(r, 5000));
                    await load();
                    const st = data?.jobs?.[pollKey]?.state;
                    if (st === "done" || st === "error") break;
                }
            }
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || `${key} failed`);
        } finally {
            setBusy(b => ({ ...b, [key]: false }));
        }
    };

    const bulkUpload = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".zip")) {
            toast.error("Please pick a .zip file containing your photos.");
            return;
        }
        setBulkUploading(true);
        setBulkResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: res } = await api.post("/admin/photos/bulk-upload-zip", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const c = res.counts || {};
            toast.success(
                `Uploaded ${c.uploaded_and_linked} · ${c.uploaded_unmatched} unmatched · ${c.rejected} rejected`,
                { duration: 8000 },
            );
            setBulkResult(res);
            await load();
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Bulk upload failed");
        } finally {
            setBulkUploading(false);
            if (bulkInputRef.current) bulkInputRef.current.value = "";
        }
    };

    if (loading || !data) {
        return (
            <div className="min-h-screen bg-rotary-cream flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-rotary-blue" />
            </div>
        );
    }

    const withPct = data.total_members ? Math.round((data.with_photo / data.total_members) * 100) : 0;

    return (
        <div className="min-h-screen bg-rotary-cream">
            <header className="border-b border-rotary-gold/20 bg-white sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Link to="/admin/platform" data-testid="photo-dashboard-back-link">
                            <Button variant="ghost" size="sm"><ChevronLeft className="h-4 w-4 mr-1" />Platform</Button>
                        </Link>
                        <h1 className="text-xl font-semibold text-rotary-blue">Photo command center</h1>
                    </div>
                    <Button variant="outline" size="sm" onClick={load} data-testid="photo-dashboard-refresh-btn">
                        <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard
                        icon={<Users2 className="h-5 w-5" />}
                        label="Members"
                        value={data.total_members}
                        sub={`${data.with_photo} have photos (${withPct}%)`}
                        testid="stat-total-members"
                    />
                    <StatCard
                        icon={<ImageOff className="h-5 w-5" />}
                        label="Without photo"
                        value={data.without_photo}
                        sub="need a fresh upload"
                        tone={data.without_photo > 0 ? "warn" : "good"}
                        testid="stat-without-photo"
                    />
                    <StatCard
                        icon={<AlertTriangle className="h-5 w-5" />}
                        label="Missing files"
                        value={data.missing_count}
                        sub="DB pointer, no file in storage"
                        tone={data.missing_count > 0 ? "bad" : "good"}
                        testid="stat-missing"
                    />
                    <StatCard
                        icon={<HardDrive className="h-5 w-5" />}
                        label="Storage"
                        value={data.storage.files}
                        sub={`${fmtBytes(data.storage.total_bytes)} · ${data.storage.unlinked_count} unlinked`}
                        tone={data.storage.unlinked_count > 5 ? "warn" : "default"}
                        testid="stat-storage"
                    />
                </div>

                <section className="space-y-2">
                    <h2 className="label-eyebrow">Actions</h2>
                    <div className="flex flex-wrap gap-2">
                        <input
                            ref={bulkInputRef}
                            type="file"
                            accept=".zip,application/zip,application/x-zip-compressed"
                            className="hidden"
                            data-testid="dash-bulk-upload-input"
                            onChange={(e) => bulkUpload(e.target.files?.[0])}
                        />
                        <Button
                            size="sm" className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                            onClick={() => bulkInputRef.current?.click()}
                            disabled={bulkUploading}
                            data-testid="dash-bulk-upload-btn"
                            title="Upload a .zip of edited photos — filenames matching Name_Phone.ext auto-link to members"
                        >
                            {bulkUploading
                                ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Uploading…</>
                                : <><UploadCloud className="mr-1.5 h-4 w-4" /> Bulk upload ZIP</>}
                        </Button>
                        {/* "Fix colors" removed in iter 40 — the CMYK→RGB
                            conversion here silently shrank 700 member photos
                            from 600KB to 14KB. Conversion now happens on-the-
                            fly in `/api/uploads/{filename}` (see
                            `_browser_safe_bytes` in server.py) so archival
                            originals stay intact. */}
                        {/* "Normalize" button removed in iter 40 — canonical
                            renames are disabled so the sheet's filename
                            column stays untouched. See _make_photo_persister
                            in server.py for the new upload-side policy. */}
                        <Button
                            variant="outline" size="sm" className="rounded-full"
                            onClick={() => action("Link photos", "/admin/photos/claim", "POST", "claim")}
                            disabled={busy["Link photos"] || data.jobs.claim?.state === "running"}
                            data-testid="dash-claim-btn"
                            title="Auto-link storage files to members by filename"
                        >
                            <Link2 className="mr-1.5 h-4 w-4" /> Link photos
                        </Button>
                        <Button
                            variant="outline" size="sm" className="rounded-full"
                            onClick={() => action("Rescan health", "/admin/photos/scan", "POST")}
                            disabled={busy["Rescan health"]}
                            data-testid="dash-rescan-btn"
                        >
                            <RefreshCw className="mr-1.5 h-4 w-4" /> Rescan health
                        </Button>
                        <Link to="/admin/photos/library">
                            <Button variant="outline" size="sm" className="rounded-full" data-testid="dash-library-btn">
                                <FileImage className="mr-1.5 h-4 w-4" /> Open library
                            </Button>
                        </Link>
                        <Link to="/admin/platform">
                            <Button variant="ghost" size="sm" className="rounded-full">
                                <ImagePlus className="mr-1.5 h-4 w-4" /> Bulk upload / Year toolbar
                            </Button>
                        </Link>
                    </div>
                </section>

                <section className="space-y-2">
                    <h2 className="label-eyebrow">Background jobs</h2>
                    <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
                        <JobCard title="Link photos (claim)" job={data.jobs.claim} testid="job-claim" />
                        <JobCard title="Normalize filenames" job={data.jobs.normalize} testid="job-normalize" />
                        <JobCard title="Color mode fix" job={data.jobs.colorfix} testid="job-colorfix" />
                        <JobCard title="Drive photo sync" job={data.jobs.drive_photos} testid="job-drive" />
                    </div>
                </section>

                {bulkResult && (
                    <section className="space-y-2" data-testid="panel-bulk-upload-result">
                        <h2 className="label-eyebrow">Last bulk upload</h2>
                        <Card className="p-4 border border-rotary-blue/30 bg-rotary-blue/5">
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-800">
                                <span><b>{bulkResult.counts.uploaded_and_linked}</b> uploaded &amp; linked</span>
                                <span className="text-amber-700"><b>{bulkResult.counts.uploaded_unmatched}</b> uploaded but unmatched (no member)</span>
                                <span className="text-red-700"><b>{bulkResult.counts.rejected}</b> rejected (below floor / bad ext)</span>
                            </div>
                            {bulkResult.rejected?.length > 0 && (
                                <details className="mt-3" data-testid="bulk-rejected-details">
                                    <summary className="cursor-pointer text-xs uppercase tracking-wide text-red-700">
                                        Rejected files ({bulkResult.rejected.length})
                                    </summary>
                                    <ul className="mt-2 max-h-40 overflow-auto text-xs text-slate-700 space-y-0.5">
                                        {bulkResult.rejected.slice(0, 50).map((r) => (
                                            <li key={`${r.filename}|${r.reason}`}><code>{r.filename}</code> — {r.reason}{r.size ? ` (${fmtBytes(r.size)})` : ""}</li>
                                        ))}
                                    </ul>
                                </details>
                            )}
                            {bulkResult.unmatched?.length > 0 && (
                                <details className="mt-2" data-testid="bulk-unmatched-details">
                                    <summary className="cursor-pointer text-xs uppercase tracking-wide text-amber-700">
                                        Unmatched files ({bulkResult.unmatched.length}) — try renaming or use Link photos
                                    </summary>
                                    <ul className="mt-2 max-h-40 overflow-auto text-xs text-slate-700 space-y-0.5">
                                        {bulkResult.unmatched.slice(0, 50).map((r) => (
                                            <li key={r.filename}><code>{r.filename}</code> — {fmtBytes(r.size)}</li>
                                        ))}
                                    </ul>
                                </details>
                            )}
                        </Card>
                    </section>
                )}
                {(data.missing_samples?.length || data.storage.unlinked_samples?.length) ? (
                    <section className="grid gap-3 lg:grid-cols-2">
                        {data.missing_samples?.length > 0 && (
                            <Card className="p-4 border border-red-200" data-testid="panel-missing-samples">
                                <div className="flex items-center gap-2 text-red-700 font-medium">
                                    <AlertTriangle className="h-4 w-4" /> Missing files ({data.missing_count})
                                </div>
                                <ul className="mt-2 space-y-1 text-sm text-slate-600">
                                    {data.missing_samples.map((m) => (
                                        <li key={m.filename || m.name} className="flex justify-between gap-2">
                                            <span className="truncate">{m.name}</span>
                                            <span className="text-xs text-slate-400 shrink-0">{m.filename}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        )}
                        {data.storage.unlinked_samples?.length > 0 && (
                            <Card className="p-4 border border-amber-200" data-testid="panel-unlinked-samples">
                                <div className="flex items-center gap-2 text-amber-700 font-medium">
                                    <Link2 className="h-4 w-4" /> Unlinked files ({data.storage.unlinked_count})
                                </div>
                                <ul className="mt-2 space-y-1 text-sm text-slate-600 font-mono text-xs">
                                    {data.storage.unlinked_samples.map((fn) => (
                                        <li key={fn} className="truncate">{fn}</li>
                                    ))}
                                </ul>
                                <p className="mt-2 text-xs text-slate-500">
                                    Try Link photos — filenames like <code>Name_Phone.jpg</code>, <code>image1_&lt;phone&gt;.jpg</code>, or <code>&lt;phone&gt;_image1.jpg</code> will auto-link.
                                </p>
                            </Card>
                        )}
                    </section>
                ) : null}
            </main>
        </div>
    );
}
